import {$isMarkNode, $unwrapMarkNode} from '@lexical/mark';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {mergeRegister} from '@lexical/utils';
import {$isHighlightNode} from 'components/DSYS/editor/nodes/HighlightNode';
import {ThreadAwareTextNode} from 'components/DSYS/editor/nodes/ThreadAwareTextNode';
import {$getRoot, $isElementNode, COMMAND_PRIORITY_LOW, LexicalNode, PASTE_COMMAND} from 'lexical';
import {useEffect} from 'react';

const unwrapHighlightNode = (node: LexicalNode) => {
  if (!$isHighlightNode(node)) return;

  const children = node.getChildren();
  children.forEach((child) => {
    node.insertBefore(child);
  });
  node.remove();
};

export const StripUnknownCommentThreadsPlugin = ({
  allowedThreadIds,
}: {
  allowedThreadIds: string[];
}): null => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const allowedIds = new Set(allowedThreadIds.filter(Boolean));

    const sanitizeComments = () => {
      editor.update(() => {
        const root = $getRoot();

        const sanitizeNode = (node: LexicalNode) => {
          if ($isElementNode(node)) {
            node.getChildren().forEach(sanitizeNode);
          }

          if ($isHighlightNode(node)) {
            const ids = node.getHighlightIds();
            const validIds = ids.filter((id) => allowedIds.has(id));

            if (validIds.length === 0) {
              unwrapHighlightNode(node);
              return;
            }

            if (validIds.length !== ids.length) {
              ids.forEach((id) => {
                if (!allowedIds.has(id)) {
                  node.removeHighlightId(id);
                }
              });
            }
            return;
          }

          if ($isMarkNode(node)) {
            const ids = node.getIDs();
            const validIds = ids.filter((id) => allowedIds.has(id));

            if (validIds.length === 0) {
              $unwrapMarkNode(node);
              return;
            }

            if (validIds.length !== ids.length) {
              node.setIDs(validIds);
            }
          }

          if (node instanceof ThreadAwareTextNode) {
            const threadId = node.getThreadId();
            if (threadId && !allowedIds.has(threadId)) {
              node.setThreadId(undefined);
            }
          }
        };

        root.getChildren().forEach(sanitizeNode);
      });
    };

    // Clean any pre-existing content (e.g., initial value)
    sanitizeComments();

    return mergeRegister(
      editor.registerCommand(
        PASTE_COMMAND,
        () => {
          // Allow Lexical to handle the paste, then sanitize the inserted nodes
          setTimeout(sanitizeComments);
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [allowedThreadIds, editor]);

  return null;
};
